import { eq, and, sql, isNull, desc, not } from "drizzle-orm";
import { DrizzleD1Database } from "drizzle-orm/d1";
import { numberingSeries } from "../schemas/numbering-series";
import type { NumberingSeries } from "../schemas/numbering-series";
import { generateUlid, createTimestamps, updateTimestamps } from "@kubdee/shared";

// ============================================================================
// runAtomicBatch — transactional multi-statement helper.
//
// In production D1's adapter exposes `db.batch([...])` which wraps every
// statement in a single D1 transaction (all-or-nothing). The unit tests
// here run against better-sqlite3 (via drizzle-orm/better-sqlite3), which
// does NOT implement `.batch`. This shim prefers the real batch path when
// available and falls back to sequential execution otherwise.
//
// ⚠️ Caveat: the sequential fallback path is NOT atomic. It exists purely
// so tests can exercise the code under the more ergonomic adapter — the
// atomic invariants are only actually enforced in production. Don't rely
// on the test path to catch half-commit bugs; use `pnpm deploy:staging`
// smoke to see the real behaviour.
// ============================================================================
type D1BatchCapable = {
  batch?: (
    statements: readonly unknown[],
  ) => Promise<readonly unknown[]>;
};

async function runAtomicBatch(
  db: DrizzleD1Database,
  statements: readonly unknown[],
): Promise<void> {
  const capable = db as unknown as D1BatchCapable;
  if (typeof capable.batch === "function") {
    await capable.batch(statements);
    return;
  }
  // Test-only fallback: sequential execution. Drizzle query builders are
  // thenable — awaiting them runs the query. Order matches the array.
  for (const stmt of statements) {
    await (stmt as Promise<unknown>);
  }
}

// ============================================================================
// Types
// ============================================================================

export interface GetNextNumberParams {
  module: string;
  doctype: string;
  company_abbreviation: string;
  companyId?: string;
  referenceDate?: Date;
}

export interface InitSeriesParams {
  module: string;
  doctype: string;
  prefix: string;
  companyId?: string;
  resetEvery?: "never" | "yearly" | "monthly";
}

export interface UpdateSeriesParams {
  prefix?: string;
  resetEvery?: "never" | "yearly" | "monthly";
  isActive?: boolean;
  autoGenerate?: boolean;
}

export interface ListSeriesParams {
  companyId?: string;
}

export interface GenerateUniqueDocumentNumberParams extends GetNextNumberParams {
  /** Physical table that stores the generated document number. */
  targetTableName: string;
  /** Column that receives the generated document number. */
  targetColumnName: string;
  /**
   * Optional company column when uniqueness is scoped by company. Most current
   * ERP document numbers are globally unique, so callers normally omit this.
   */
  targetCompanyColumnName?: string;
  maxAttempts?: number;
}

const DEFAULT_UNIQUE_NUMBER_MAX_ATTEMPTS = 5;
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

// ============================================================================
// listSeries — List all numbering series with optional companyId filter
// ============================================================================

export async function listSeries(
  db: DrizzleD1Database,
  params?: ListSeriesParams
): Promise<NumberingSeries[]> {
  if (params?.companyId) {
    // Per-company-wins resolution mirrors `findSeries()`. We fetch both
    // per-company AND platform-default (NULL) rows, then collapse: when a
    // `(module, doctype)` tuple has a per-company row, the matching NULL
    // row is hidden so the legacy /settings/numbering page doesn't render
    // the same logical series twice. The NULL row is still in the database
    // — `findSeries()` (and the auto-fork on first view) still relies on
    // it for any company that hasn't been seeded yet.
    //
    // With the eager fork in each module's seed (#423 follow-up), every
    // freshly-created company has per-company rows from day 1, so this
    // collapse usually drops every NULL row. But existing tenants from
    // before the eager-fork landed may still have unforked combos — those
    // surface here as the NULL fallback row.
    const rows = await db
      .select()
      .from(numberingSeries)
      .where(
        sql`${numberingSeries.companyId} = ${params.companyId} OR ${numberingSeries.companyId} IS NULL`
      )
      .all();

    const perCompanyKeys = new Set<string>();
    for (const row of rows) {
      if (row.companyId === params.companyId) {
        perCompanyKeys.add(`${row.module}:${row.doctype}`);
      }
    }
    return rows.filter(
      (row) =>
        row.companyId === params.companyId ||
        !perCompanyKeys.has(`${row.module}:${row.doctype}`),
    );
  }

  return db.select().from(numberingSeries).all();
}

// ============================================================================
// getNextNumber — Get next number in series (preview only, does NOT increment)
// ============================================================================

export async function getNextNumber(
  db: DrizzleD1Database,
  params: GetNextNumberParams
): Promise<string> {
  const series = await findSeries(db, params.module, params.doctype, params.companyId);

  if (!series) {
    throw new Error(
      `NOT_FOUND: Numbering series not found for ${params.module}/${params.doctype}`
    );
  }

  // Calculate effective current value considering auto-reset
  const effectiveValue = getEffectiveNextValue(series, params.referenceDate);
  return formatNumber(series.prefix, effectiveValue, params.company_abbreviation, params.referenceDate);
}

// ============================================================================
// getCurrentNumber — Format the already-claimed current value without increment
// ============================================================================

export async function getCurrentNumber(
  db: DrizzleD1Database,
  params: GetNextNumberParams,
): Promise<string> {
  const series = await findSeries(db, params.module, params.doctype, params.companyId);

  if (!series) {
    throw new Error(
      `NOT_FOUND: Numbering series not found for ${params.module}/${params.doctype}`,
    );
  }

  return formatNumber(
    series.prefix,
    series.currentValue,
    params.company_abbreviation,
    params.referenceDate,
  );
}

// ============================================================================
// generateNextNumber — Generate and consume next number (auto-increment)
//
// Uses SQL UPDATE ... SET current_value = current_value + 1 for atomicity.
// This prevents race conditions in concurrent access.
// Handles auto-reset for yearly/monthly series.
// ============================================================================

export async function generateNextNumber(
  db: DrizzleD1Database,
  params: GetNextNumberParams
): Promise<string> {
  let series = await findSeries(db, params.module, params.doctype, params.companyId);

  if (!series) {
    throw new Error(
      `NOT_FOUND: Numbering series not found for ${params.module}/${params.doctype}`
    );
  }

  // Auto-fork on first generateNextNumber for an existing tenant: if
  // findSeries fell back to the platform-default NULL row but a companyId
  // was supplied, copy the NULL row into a per-company row before the
  // company-scoped UPDATE below. Without this, claiming values for
  // `companyId = X` would match 0 rows (NULL row has
  // company_id IS NULL), and re-fetch returns the still-zero NULL row →
  // every call returns the prefix with value=0 (e.g. "S-00000").
  //
  // Companies created post-#423 land their per-company row eagerly via
  // `forkDefaultsForCompany()` from each module's `core.module.enabled`
  // hook. This branch handles the LAZY case for tenants that existed
  // before that hook was wired up. Mirrors the auto-fork behaviour in
  // `listDoctypeSeries()`.
  if (series.companyId === null && params.companyId) {
    await forkPlatformDefaultsToCompany(db, {
      module: params.module,
      doctype: params.doctype,
      companyId: params.companyId,
    });
    const reread = await findSeries(db, params.module, params.doctype, params.companyId);
    if (!reread) {
      throw new Error(
        `NOT_FOUND: Numbering series not found after fork for ${params.module}/${params.doctype}`
      );
    }
    series = reread;
  }

  const now = params.referenceDate ?? new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const nowIso = now.toISOString();

  let shouldReset = false;

  // Check if reset is needed
  if (series.resetEvery === "yearly") {
    if (series.lastResetYear !== currentYear) {
      shouldReset = true;
    }
  } else if (series.resetEvery === "monthly") {
    if (series.lastResetYear !== currentYear || series.lastResetMonth !== currentMonth) {
      shouldReset = true;
    }
  }

  const updated = await claimNumberingValues(db, {
    series,
    count: 1,
    currentYear,
    currentMonth,
    shouldReset,
  });

  if (!updated) {
    // Another request reset this series between our read and update. Re-read
    // and claim the next value from the fresh counter instead of handing out
    // a duplicate reset value.
    return generateNextNumber(db, params);
  }

  return formatNumber(
    updated.prefix,
    updated.currentValue,
    params.company_abbreviation,
    now,
  );
}

// ============================================================================
// generateNextNumberBatch — Generate N numbers in a single atomic operation
//
// Instead of calling generateNextNumber N times (3 DB ops each), this:
//   1. Finds the series once
//   2. Handles reset if needed
//   3. Atomically increments currentValue by `count` in one UPDATE
//   4. Formats all numbers in-memory
// Total: 2 DB reads + 1 DB write → O(1) regardless of count
// ============================================================================

export async function generateNextNumberBatch(
  db: DrizzleD1Database,
  params: GetNextNumberParams,
  count: number
): Promise<string[]> {
  if (count <= 0) return [];

  let series = await findSeries(db, params.module, params.doctype, params.companyId);

  if (!series) {
    throw new Error(
      `NOT_FOUND: Numbering series not found for ${params.module}/${params.doctype}`
    );
  }

  // Same auto-fork branch as `generateNextNumber()` — see that function for
  // the full rationale. Without this, batched numbering on existing tenants
  // (e.g. salary-slip generation that pre-dates the eager-fork hook) would
  // hand out N copies of the prefix-with-value-zero (e.g. "SS-2026-04-00000")
  // because the UPDATE strictly filters by companyId and matches 0 rows.
  if (series.companyId === null && params.companyId) {
    await forkPlatformDefaultsToCompany(db, {
      module: params.module,
      doctype: params.doctype,
      companyId: params.companyId,
    });
    const reread = await findSeries(db, params.module, params.doctype, params.companyId);
    if (!reread) {
      throw new Error(
        `NOT_FOUND: Numbering series not found after fork for ${params.module}/${params.doctype}`
      );
    }
    series = reread;
  }

  const now = params.referenceDate ?? new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  let startValue: number;

  const shouldReset =
    (series.resetEvery === "yearly" && series.lastResetYear !== currentYear) ||
    (series.resetEvery === "monthly" &&
      (series.lastResetYear !== currentYear || series.lastResetMonth !== currentMonth));

  const updated = await claimNumberingValues(db, {
    series,
    count,
    currentYear,
    currentMonth,
    shouldReset,
  });

  if (!updated) {
    // Same reset race handling as generateNextNumber().
    return generateNextNumberBatch(db, params, count);
  }

  startValue = updated.currentValue - count + 1;

  const numbers: string[] = [];
  for (let i = 0; i < count; i++) {
    numbers.push(formatNumber(updated.prefix, startValue + i, params.company_abbreviation, now));
  }
  return numbers;
}

// ============================================================================
// generateUniqueDocumentNumber — Generate a number and repair drift on collision
// ============================================================================

export async function generateUniqueDocumentNumber(
  db: DrizzleD1Database,
  params: GenerateUniqueDocumentNumberParams,
): Promise<string> {
  const maxAttempts = params.maxAttempts ?? DEFAULT_UNIQUE_NUMBER_MAX_ATTEMPTS;
  const target = validateDocumentNumberTarget(params);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const documentNo = await generateNextNumber(db, params);
    const existing = await documentNumberExists(db, target, {
      documentNo,
      companyId: params.companyId,
    });

    if (!existing) {
      return documentNo;
    }

    console.warn("[numbering] generated duplicate document number; repairing series drift", {
      module: params.module,
      doctype: params.doctype,
      companyId: params.companyId ?? null,
      table: params.targetTableName,
      column: params.targetColumnName,
      documentNo,
      attempt: attempt + 1,
    });

    const maxExisting = await getMaxExistingTrailingDocumentNumber(db, target, {
      documentNo,
      companyId: params.companyId,
    });
    if (maxExisting !== null) {
      await bumpSeriesCurrentValue(db, params, maxExisting);
    }
  }

  throw new Error(
    "CONFLICT: Unable to generate a unique document number. Please retry."
  );
}

// ============================================================================
// initSeries — Initialize a numbering series if it doesn't exist
// ============================================================================

export async function initSeries(
  db: DrizzleD1Database,
  params: InitSeriesParams
): Promise<{ id: string; created: boolean }> {
  // Check if series already exists
  const existing = await findSeries(db, params.module, params.doctype, params.companyId);

  if (existing) {
    return { id: existing.id, created: false };
  }

  // Create new series
  const id = generateUlid();

  await db.insert(numberingSeries).values({
    id,
    module: params.module,
    doctype: params.doctype,
    prefix: params.prefix,
    currentValue: 0,
    companyId: params.companyId ?? null,
    resetEvery: params.resetEvery ?? "yearly",
    isActive: 1,
    ...createTimestamps(),
  });

  return { id, created: true };
}

// ============================================================================
// updateSeries — Update prefix, resetEvery for a series
// ============================================================================

export async function updateSeries(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  params: UpdateSeriesParams,
  companyId?: string
): Promise<NumberingSeries | null> {
  const series = await findSeries(db, module, doctype, companyId);

  if (!series) {
    return null;
  }

  const updates: Record<string, unknown> = {
    ...updateTimestamps(),
  };

  if (params.prefix !== undefined) updates.prefix = params.prefix;
  if (params.resetEvery !== undefined) updates.resetEvery = params.resetEvery;
  if (params.isActive !== undefined) updates.isActive = params.isActive ? 1 : 0;
  if (params.autoGenerate !== undefined) updates.autoGenerate = params.autoGenerate ? 1 : 0;

  // Use the actual found series ID to update (handles global fallback correctly)
  await db.update(numberingSeries).set(updates).where(eq(numberingSeries.id, series.id));

  return (await findSeries(db, module, doctype, companyId)) ?? null;
}

// ============================================================================
// deleteSeries — Delete a numbering series
// ============================================================================

export async function deleteSeries(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  companyId?: string
): Promise<boolean> {
  const series = await findSeries(db, module, doctype, companyId);

  if (!series) {
    return false;
  }

  await db.delete(numberingSeries).where(eq(numberingSeries.id, series.id));
  return true;
}

// ============================================================================
// previewNumber — Preview formatted number without incrementing
// ============================================================================

export async function previewNumber(
  db: DrizzleD1Database,
  params: GetNextNumberParams
): Promise<{ preview: string; currentValue: number; nextValue: number }> {
  const series = await findSeries(db, params.module, params.doctype, params.companyId);

  if (!series) {
    throw new Error(
      `NOT_FOUND: Numbering series not found for ${params.module}/${params.doctype}`
    );
  }

  const nextValue = getEffectiveNextValue(series);
  const preview = formatNumber(series.prefix, nextValue, params.company_abbreviation);

  return {
    preview,
    currentValue: series.currentValue,
    nextValue,
  };
}

// ============================================================================
// Internal helpers
// ============================================================================

interface ValidatedDocumentNumberTarget {
  tableName: string;
  columnName: string;
  companyColumnName?: string;
}

function validateDocumentNumberTarget(
  params: GenerateUniqueDocumentNumberParams,
): ValidatedDocumentNumberTarget {
  assertSqlIdentifier(params.targetTableName, "targetTableName");
  assertSqlIdentifier(params.targetColumnName, "targetColumnName");
  if (params.targetCompanyColumnName) {
    assertSqlIdentifier(params.targetCompanyColumnName, "targetCompanyColumnName");
  }

  return {
    tableName: params.targetTableName,
    columnName: params.targetColumnName,
    companyColumnName: params.targetCompanyColumnName,
  };
}

function assertSqlIdentifier(value: string, label: string): void {
  if (!IDENTIFIER_RE.test(value)) {
    throw new Error(`VALIDATION_ERROR: Invalid ${label} "${value}"`);
  }
}

function quoteIdentifier(value: string): string {
  assertSqlIdentifier(value, "SQL identifier");
  return `"${value}"`;
}

async function documentNumberExists(
  db: DrizzleD1Database,
  target: ValidatedDocumentNumberTarget,
  params: {
    documentNo: string;
    companyId?: string;
  },
): Promise<boolean> {
  const table = sql.raw(quoteIdentifier(target.tableName));
  const column = sql.raw(quoteIdentifier(target.columnName));
  const companyClause =
    target.companyColumnName && params.companyId
      ? sql` AND ${sql.raw(quoteIdentifier(target.companyColumnName))} = ${params.companyId}`
      : sql``;

  const row = await rawFirst<{ found: number }>(
    db,
    sql`
      SELECT 1 AS found
      FROM ${table}
      WHERE ${column} = ${params.documentNo}
      ${companyClause}
      LIMIT 1
    `,
  );

  return !!row;
}

async function getMaxExistingTrailingDocumentNumber(
  db: DrizzleD1Database,
  target: ValidatedDocumentNumberTarget,
  params: {
    documentNo: string;
    companyId?: string;
  },
): Promise<number | null> {
  const match = params.documentNo.match(/^(.*?)(\d+)$/);
  if (!match) return null;

  const [, prefix, numericPart] = match;
  if (prefix === undefined || !numericPart) return null;

  const table = sql.raw(quoteIdentifier(target.tableName));
  const column = sql.raw(quoteIdentifier(target.columnName));
  const companyClause =
    target.companyColumnName && params.companyId
      ? sql` AND ${sql.raw(quoteIdentifier(target.companyColumnName))} = ${params.companyId}`
      : sql``;
  const numberStart = prefix.length + 1;
  const totalLength = prefix.length + numericPart.length;

  const row = await rawFirst<{ maxValue: number | null }>(
    db,
    sql`
      SELECT MAX(CAST(substr(${column}, ${numberStart}) AS INTEGER)) AS maxValue
      FROM ${table}
      WHERE substr(${column}, 1, ${prefix.length}) = ${prefix}
        AND length(${column}) = ${totalLength}
        AND substr(${column}, ${numberStart}) NOT GLOB '*[^0-9]*'
        ${companyClause}
    `,
  );

  return row?.maxValue ?? null;
}

async function bumpSeriesCurrentValue(
  db: DrizzleD1Database,
  params: GetNextNumberParams,
  currentValue: number,
): Promise<void> {
  const companyCondition = params.companyId
    ? eq(numberingSeries.companyId, params.companyId)
    : isNull(numberingSeries.companyId);

  await db
    .update(numberingSeries)
    .set({
      currentValue: sql`CASE WHEN ${numberingSeries.currentValue} < ${currentValue} THEN ${currentValue} ELSE ${numberingSeries.currentValue} END`,
      updatedAt: new Date().toISOString(),
    })
    .where(
      and(
        eq(numberingSeries.module, params.module),
        eq(numberingSeries.doctype, params.doctype),
        companyCondition,
        eq(numberingSeries.isDefault, 1),
      ),
    );
}

async function rawFirst<T>(
  db: DrizzleD1Database,
  query: ReturnType<typeof sql>,
): Promise<T | undefined> {
  const runner = db as unknown as {
    get?: (query: ReturnType<typeof sql>) => Promise<T | undefined> | T | undefined;
    all?: (query: ReturnType<typeof sql>) => Promise<unknown> | unknown;
    run?: (query: ReturnType<typeof sql>) => Promise<unknown> | unknown;
  };

  if (typeof runner.get === "function") {
    return (await runner.get(query)) ?? undefined;
  }

  const result = typeof runner.all === "function"
    ? await runner.all(query)
    : await runner.run?.(query);
  const rows = (Array.isArray(result)
    ? result
    : ((result as { results?: unknown[]; rows?: unknown[] } | undefined)?.results
      ?? (result as { rows?: unknown[] } | undefined)?.rows
      ?? [])) as T[];

  return rows[0];
}

/**
 * Find a series by module/doctype, optionally scoped by companyId.
 * Falls back to global series (companyId IS NULL) if company-specific not found.
 *
 * Since A5 (#353) multiple series can exist per (module, doctype, companyId).
 * We select the one with `is_default = 1`. Order-by `isDefault DESC` makes the
 * pick deterministic even for legacy rows where the flag was absent before
 * migration 003 (they default to 1 via the column DEFAULT).
 */
async function findSeries(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  companyId?: string
): Promise<NumberingSeries | undefined> {
  // If companyId provided, try company-specific first
  if (companyId) {
    const companySpecific = await db
      .select()
      .from(numberingSeries)
      .where(
        and(
          eq(numberingSeries.module, module),
          eq(numberingSeries.doctype, doctype),
          eq(numberingSeries.companyId, companyId)
        )
      )
      .orderBy(desc(numberingSeries.isDefault))
      .get();

    if (companySpecific) return companySpecific;
  }

  // Fallback to global series (companyId IS NULL)
  return db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, module),
        eq(numberingSeries.doctype, doctype),
        isNull(numberingSeries.companyId)
      )
    )
    .orderBy(desc(numberingSeries.isDefault))
    .get();
}

async function claimNumberingValues(
  db: DrizzleD1Database,
  params: {
    series: NumberingSeries;
    count: number;
    currentYear: number;
    currentMonth: number;
    shouldReset: boolean;
  },
): Promise<{ prefix: string; currentValue: number } | undefined> {
  const baseWhere = eq(numberingSeries.id, params.series.id);
  const whereCondition = params.shouldReset
    ? and(
        baseWhere,
        buildResetRequiredCondition(
          params.series.resetEvery,
          params.currentYear,
          params.currentMonth,
        ),
      )
    : baseWhere;

  const rows = await db
    .update(numberingSeries)
    .set({
      currentValue: params.shouldReset
        ? params.count
        : sql`${numberingSeries.currentValue} + ${params.count}`,
      ...(params.shouldReset
        ? {
            lastResetYear: params.currentYear,
            lastResetMonth: params.currentMonth,
          }
        : {}),
      ...updateTimestamps(),
    })
    .where(whereCondition)
    .returning({
      prefix: numberingSeries.prefix,
      currentValue: numberingSeries.currentValue,
    });

  return rows[0];
}

function buildResetRequiredCondition(
  resetEvery: NumberingSeries["resetEvery"],
  currentYear: number,
  currentMonth: number,
) {
  if (resetEvery === "yearly") {
    return sql`(${numberingSeries.lastResetYear} IS NULL OR ${numberingSeries.lastResetYear} != ${currentYear})`;
  }
  if (resetEvery === "monthly") {
    return sql`
      (${numberingSeries.lastResetYear} IS NULL OR
      ${numberingSeries.lastResetYear} != ${currentYear} OR
      ${numberingSeries.lastResetMonth} IS NULL OR
      ${numberingSeries.lastResetMonth} != ${currentMonth})
    `;
  }
  return sql`1 = 1`;
}

/**
 * Calculate the effective next value considering auto-reset.
 * Used for preview (does not persist).
 */
function getEffectiveNextValue(series: NumberingSeries, referenceDate?: Date): number {
  const now = referenceDate ?? new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (series.resetEvery === "yearly" && series.lastResetYear !== currentYear) {
    return 1; // Would reset
  }

  if (
    series.resetEvery === "monthly" &&
    (series.lastResetYear !== currentYear || series.lastResetMonth !== currentMonth)
  ) {
    return 1; // Would reset
  }

  return series.currentValue + 1;
}

// ============================================================================
// formatNumber — Format number string from prefix template + sequential value
//
// Supported placeholders:
//   {company_abbr} → company abbreviation (e.g., "KCR")
//   {BRANCH}       → branch code (passed by caller, empty string if omitted)
//   {YYYY}         → full year (e.g., "2026")
//   {YY}           → short year (e.g., "26")
//   {MM}           → zero-padded month (e.g., "03")
//   {DD}           → zero-padded day (e.g., "21")
//   {A}            → random uppercase letter (A-Z)
//   {0}            → random digit (0-9)
//   #              → sequential digit (count of # = padding width) — legacy
//   {#####}        → Same as ##### but enclosed (A5 mockup uses this form)
//
// Examples:
//   "RB-{YYYY}-#####"                   → "RB-2026-00001"
//   "INV-{YYYY}-{#####}"                → "INV-2026-00001"
//   "{A}{A}{0}{0}{0}{0}"                → "MX0345" (pure random, no sequence)
//   "{A}{A}-###"                        → "KX-001" (random + sequential)
//   "{company_abbr}-{YYYY}{MM}-####"    → "KCR-202603-0001"
//   "POS-{BRANCH}-{######}"             → "POS-BKK-000842"
//   "CUST-#####"                        → "CUST-00001"
// ============================================================================

function formatNumber(
  prefix: string,
  value: number,
  companyAbbreviation: string,
  referenceDate?: Date,
): string {
  return previewPattern(prefix, {
    nextValue: value,
    companyAbbr: companyAbbreviation,
    // generateNextNumber path never carries a branch — kept ambiguous with ""
    // so the legacy call sites (quotations, invoices, bookings) keep working.
    branch: "",
    now: referenceDate,
  });
}

// ----------------------------------------------------------------------------
// previewPattern — Pure, side-effect-free preview helper.
//
// Exported for the Doctype Admin Naming tab (issue A5 #353). Keeps the
// rendering rules in one place so the UI's live-preview and the server-side
// `generateNextNumber` can NEVER drift.
//
// Rules:
//   - {YYYY}/{YY}/{MM}/{DD}  — resolved from `opts.now` (default: Date.now()).
//   - {company_abbr}         — from `opts.companyAbbr`.
//   - {BRANCH}               — from `opts.branch`.
//   - {A}/{0}                — randomised here to match generateNextNumber
//     behaviour. Preview is allowed to flicker on re-render — that's correct
//     because the server ALSO rolls fresh randomness each time.
//   - # sequence (bare ##### OR braced {#####}) — padded to that width using
//     `opts.nextValue`. If both bare and braced appear in one pattern, only
//     the FIRST occurrence is replaced (same as the legacy formatter).
//
// Deliberately does not try to "fill in missing tokens" — if the pattern has
// no # and no random tokens, the result is the pattern itself, which is the
// expected behaviour for template-only ids (e.g. `"DRAFT"`).
// ----------------------------------------------------------------------------

/** Options for `previewPattern`. All fields optional for flexibility. */
export interface PreviewPatternOptions {
  /** Sequential value to pad into the first #-run. Defaults to 1. */
  nextValue?: number;
  /** Replaces `{company_abbr}`. Empty string if omitted. */
  companyAbbr?: string;
  /** Replaces `{BRANCH}`. Empty string if omitted. */
  branch?: string;
  /** Clock source for year/month/day tokens. Defaults to Date.now(). */
  now?: Date;
  /**
   * Deterministic random provider for `{A}` / `{0}` tokens. Accepts `()=> number`
   * returning [0,1). Defaults to `Math.random`. Exposed for tests so preview
   * assertions don't have to tolerate flicker.
   */
  random?: () => number;
}

export function previewPattern(
  pattern: string,
  options: PreviewPatternOptions = {},
): string {
  const now = options.now ?? new Date();
  const nextValue = options.nextValue ?? 1;
  const companyAbbr = options.companyAbbr ?? "";
  const branch = options.branch ?? "";
  const random = options.random ?? Math.random;

  const year = now.getFullYear().toString();
  const shortYear = year.slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const day = now.getDate().toString().padStart(2, "0");

  // Deterministic token substitutions first — case-sensitive, matching the
  // legacy formatter. We do NOT use /i because `{yyyy}` and `{YYYY}` carry
  // different intent in some ERPNext-style templates (none used here yet).
  let formatted = pattern
    .replace(/\{company_abbr\}/g, companyAbbr)
    .replace(/\{BRANCH\}/g, branch)
    .replace(/\{YYYY\}/g, year)
    .replace(/\{YY\}/g, shortYear)
    .replace(/\{MM\}/g, month)
    .replace(/\{DD\}/g, day);

  // Random tokens — both A and 0 callsites use the injected `random()` so
  // tests can snapshot with a seeded generator.
  formatted = formatted.replace(/\{A\}/g, () =>
    String.fromCharCode(65 + Math.floor(random() * 26)),
  );
  formatted = formatted.replace(/\{0\}/g, () =>
    String(Math.floor(random() * 10)),
  );

  // Braced hash form first — `{#####}` is the mockup's canonical shape.
  // Matches 1-10 `#` inside braces. Only the first occurrence is replaced
  // (consistent with the bare form below — preserves legacy semantics).
  const bracedMatch = formatted.match(/\{(#+)\}/);
  if (bracedMatch && bracedMatch[1]) {
    const padded = nextValue.toString().padStart(bracedMatch[1].length, "0");
    formatted = formatted.replace(/\{#+\}/, padded);
  } else {
    // Bare hash form — legacy. `##### → 00001`.
    const bareMatch = formatted.match(/#+/);
    if (bareMatch) {
      const padded = nextValue.toString().padStart(bareMatch[0].length, "0");
      formatted = formatted.replace(/#+/, padded);
    }
  }

  return formatted;
}

// ============================================================================
// Multi-series CRUD — A5 (#353). Operates on the `core_numbering_series` table
// after migration 003 extended it with (is_default, series_name).
//
// Scoping:
//   - All helpers below are company-scoped. Callers MUST pass companyId; the
//     Doctype Admin Naming tab reads it from the auth store. Global rows
//     (company_id IS NULL) are not managed through these helpers — they exist
//     only as the fallback target of initSeries / legacy generateNextNumber.
//
// Default invariant:
//   - At most one row per (module, doctype, company_id) has `is_default = 1`.
//     Enforced by the partial UNIQUE index in migration 003, and maintained
//     atomically by setDefaultSeriesById (unset-others → set-target).
// ============================================================================

export interface DoctypeSeriesRow {
  id: string;
  module: string;
  doctype: string;
  seriesName: string | null;
  prefix: string;
  currentValue: number;
  companyId: string | null;
  resetEvery: "never" | "yearly" | "monthly";
  autoGenerate: number;
  isActive: number;
  isDefault: number;
  updatedAt: string;
}

export interface CreateDoctypeSeriesParams {
  module: string;
  doctype: string;
  companyId: string;
  prefix: string;
  seriesName?: string | null;
  resetEvery?: "never" | "yearly" | "monthly";
  autoGenerate?: boolean;
  /** If true, this new series becomes the default (un-flagging any other). */
  makeDefault?: boolean;
  createdBy?: string;
}

export interface UpdateDoctypeSeriesParams {
  prefix?: string;
  seriesName?: string | null;
  resetEvery?: "never" | "yearly" | "monthly";
  autoGenerate?: boolean;
  isActive?: boolean;
  updatedBy?: string;
}

/**
 * List numbering series for a doctype, surfaced to the Doctype Admin UI.
 *
 * Auto-fork semantics (ERPNext "Customize Form" parity):
 *   When the company has no per-company rows, copy each platform-default
 *   row (`company_id = NULL`) seeded by `seedDefaultSeries()` into a
 *   per-company row scoped to `params.companyId`. The fresh fork
 *   preserves prefix / resetEvery / seriesName / autoGenerate / isActive /
 *   isDefault / currentValue verbatim — admin opens the page and sees
 *   "their" series with the platform default already filled in. From that
 *   point forward every edit / counter increment lands on the per-company
 *   row; the NULL row stays untouched as the template for the next
 *   company that visits.
 *
 * Why fork instead of reading the NULL row directly:
 *   - Editing through the API is by `id` — exposing the NULL row to
 *     `PATCH /series/:id` lets admins accidentally mutate every other
 *     company's fallback in one click.
 *   - Deploys re-run `seedDefaultSeries()` which UPDATEs the NULL row's
 *     prefix back to the registry value; admin edits would silently
 *     vanish next deploy.
 *   - Counter divergence stays clean: each company increments only its
 *     own row.
 *
 * Concurrency: two concurrent GETs both seeing zero per-company rows
 * could try to fork at the same time. The partial UNIQUE
 * `(module, doctype, company_id) WHERE is_default = 1` makes the
 * second insert fail; we catch the error and re-read the per-company
 * rows that the winning request already wrote.
 *
 * Rows are ordered `(isDefault DESC, createdAt ASC)` so the default
 * always appears first — matches the UI grouping. Empty array remains a
 * valid result when neither layer has registered a series for this combo.
 */
export async function listDoctypeSeries(
  db: DrizzleD1Database,
  params: {
    module: string;
    doctype: string;
    companyId: string;
    /** Optional — set on `created_by` / `updated_by` for forked rows. */
    userId?: string;
  },
): Promise<DoctypeSeriesRow[]> {
  const companyRows = await db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, params.module),
        eq(numberingSeries.doctype, params.doctype),
        eq(numberingSeries.companyId, params.companyId),
      ),
    )
    .orderBy(desc(numberingSeries.isDefault), numberingSeries.createdAt);

  if (companyRows.length > 0) {
    return companyRows.map(toDoctypeSeriesRow);
  }

  // Auto-fork from platform-default rows. Empty array result means the
  // doctype simply has no series declared anywhere — return empty so the
  // UI can show the empty-state CTA.
  const forked = await forkPlatformDefaultsToCompany(db, {
    module: params.module,
    doctype: params.doctype,
    companyId: params.companyId,
    userId: params.userId,
  });

  return forked.map(toDoctypeSeriesRow);
}

function toDoctypeSeriesRow(
  r: typeof numberingSeries.$inferSelect,
): DoctypeSeriesRow {
  return {
    id: r.id,
    module: r.module,
    doctype: r.doctype,
    seriesName: r.seriesName,
    prefix: r.prefix,
    currentValue: r.currentValue,
    companyId: r.companyId,
    resetEvery: r.resetEvery as "never" | "yearly" | "monthly",
    autoGenerate: r.autoGenerate,
    isActive: r.isActive,
    isDefault: r.isDefault,
    updatedAt: r.updatedAt,
  };
}

/**
 * Copy every platform-default row (`company_id = NULL`) for one
 * `(module, doctype)` into per-company rows scoped to `companyId`.
 *
 * Caller's responsibility to verify there are NO per-company rows yet —
 * we don't re-check inside the helper to keep the read path single-pass.
 *
 * Race-safe: a concurrent fork would hit the partial UNIQUE on
 * `is_default = 1`. We catch the constraint violation, re-read the
 * per-company rows that the other request just wrote, and return those.
 *
 * Returns the freshly written (or concurrently-written) per-company
 * rows in the same `(isDefault DESC, createdAt ASC)` order
 * `listDoctypeSeries()` uses — caller can hand straight to the response.
 */
async function forkPlatformDefaultsToCompany(
  db: DrizzleD1Database,
  params: {
    module: string;
    doctype: string;
    companyId: string;
    userId?: string;
  },
): Promise<(typeof numberingSeries.$inferSelect)[]> {
  const platformRows = await db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, params.module),
        eq(numberingSeries.doctype, params.doctype),
        isNull(numberingSeries.companyId),
      ),
    );

  if (platformRows.length === 0) return [];

  const now = new Date().toISOString();
  const userId = params.userId ?? null;

  try {
    for (const src of platformRows) {
      await db.insert(numberingSeries).values({
        id: generateUlid(),
        module: src.module,
        doctype: src.doctype,
        seriesName: src.seriesName,
        prefix: src.prefix,
        currentValue: src.currentValue,
        companyId: params.companyId,
        resetEvery: src.resetEvery,
        lastResetYear: src.lastResetYear,
        lastResetMonth: src.lastResetMonth,
        autoGenerate: src.autoGenerate,
        isActive: src.isActive,
        isDefault: src.isDefault,
        createdAt: now,
        createdBy: userId,
        updatedAt: now,
        updatedBy: userId,
      });
    }
  } catch {
    // Almost certainly a partial-UNIQUE violation from a concurrent fork
    // for the same (module, doctype, companyId). The other request has
    // already written the per-company rows; fall through to re-read.
  }

  return db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, params.module),
        eq(numberingSeries.doctype, params.doctype),
        eq(numberingSeries.companyId, params.companyId),
      ),
    )
    .orderBy(desc(numberingSeries.isDefault), numberingSeries.createdAt);
}

/**
 * Eagerly fork every doctype's platform-default series into per-company rows
 * for a freshly-created company. Called from each module's
 * `core.company.created` seed hook so that fresh tenants/companies get
 * isolated counters from day 1 — without this, multi-company tenants would
 * share counters via the NULL platform-default fallback until an admin
 * manually opens each doctype in the Doctype Admin UI.
 *
 * Idempotent — re-runs (e.g. module re-enable) skip combos that already
 * have a per-company row. Doctypes WITHOUT `defaultSeries` (e.g. ULID-only
 * or non-naming doctypes) are skipped silently.
 *
 * Caller passes the module's frozen `registeredDoctypes` array directly:
 *
 *   await forkDefaultsForCompany(db, {
 *     companyId,
 *     userId,
 *     doctypes: salesDoctypes,
 *   });
 */
export async function forkDefaultsForCompany(
  db: DrizzleD1Database,
  params: {
    companyId: string;
    userId?: string;
    /** A module's `registeredDoctypes` array. Doctypes without
     *  `defaultSeries` are skipped — there's nothing to fork from. */
    doctypes: readonly {
      moduleId: string;
      id: string;
      defaultSeries?: unknown;
    }[];
  },
): Promise<{ forked: number; skipped: number }> {
  let forked = 0;
  let skipped = 0;

  for (const d of params.doctypes) {
    if (!d.defaultSeries) {
      skipped += 1;
      continue;
    }

    // Skip if a per-company row already exists for this combo. Per-company
    // rows are admin-intent territory — we never overwrite, even if the
    // seed could legitimately re-run.
    const existing = await db
      .select({ id: numberingSeries.id })
      .from(numberingSeries)
      .where(
        and(
          eq(numberingSeries.module, d.moduleId),
          eq(numberingSeries.doctype, d.id),
          eq(numberingSeries.companyId, params.companyId),
        ),
      )
      .get();

    if (existing) {
      skipped += 1;
      continue;
    }

    const fresh = await forkPlatformDefaultsToCompany(db, {
      module: d.moduleId,
      doctype: d.id,
      companyId: params.companyId,
      userId: params.userId,
    });
    if (fresh.length > 0) {
      forked += 1;
    } else {
      // No NULL platform-default row to fork from — defaultSeries is set on
      // the doctype but the registry hasn't materialised it yet (race vs
      // auto-migrate). Skip; the next listDoctypeSeries() call will fork
      // lazily once both halves are in place.
      skipped += 1;
    }
  }

  return { forked, skipped };
}

/**
 * Create a new numbering series for a doctype (company-scoped).
 *
 * If `makeDefault` is true — or if no default currently exists for this
 * (module, doctype, companyId) — the new row lands with `is_default = 1`
 * and any other default row is flipped to 0 in the SAME transaction to
 * honour the partial UNIQUE constraint. Otherwise the row is inserted as
 * `is_default = 0`.
 */
export async function createDoctypeSeries(
  db: DrizzleD1Database,
  params: CreateDoctypeSeriesParams,
): Promise<DoctypeSeriesRow> {
  const id = generateUlid();

  // Decide default-ness up front. If caller didn't ask but there's no
  // existing default, we MUST adopt — otherwise the doctype would have no
  // default and generateNextNumber would fall back to global (or fail).
  const existing = await db
    .select({ id: numberingSeries.id })
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, params.module),
        eq(numberingSeries.doctype, params.doctype),
        eq(numberingSeries.companyId, params.companyId),
        eq(numberingSeries.isDefault, 1),
      ),
    )
    .limit(1);

  const shouldBeDefault = params.makeDefault ?? existing.length === 0;

  const insertValues = {
    id,
    module: params.module,
    doctype: params.doctype,
    seriesName: params.seriesName ?? null,
    prefix: params.prefix,
    currentValue: 0,
    companyId: params.companyId,
    resetEvery: params.resetEvery ?? "yearly",
    autoGenerate: params.autoGenerate === false ? 0 : 1,
    isActive: 1,
    isDefault: shouldBeDefault ? 1 : 0,
    ...createTimestamps(params.createdBy ?? null),
  } as const;

  if (shouldBeDefault && existing.length > 0) {
    // Atomic: unset the existing default + insert the new default row in
    // ONE D1 request. Without db.batch, if the unset succeeded but the
    // insert failed (e.g. worker eviction, validation throw), the triple
    // would end up with ZERO defaults — generateNextNumber would break.
    // db.batch on drizzle-orm/d1 wraps both statements in a single D1
    // transaction — partial failure is impossible.
    await runAtomicBatch(db, [
      db
        .update(numberingSeries)
        .set({ isDefault: 0, ...updateTimestamps(params.createdBy ?? null) })
        .where(
          and(
            eq(numberingSeries.module, params.module),
            eq(numberingSeries.doctype, params.doctype),
            eq(numberingSeries.companyId, params.companyId),
            eq(numberingSeries.isDefault, 1),
          ),
        ),
      db.insert(numberingSeries).values(insertValues),
    ]);
  } else {
    // No unset needed (not becoming default OR no existing default). Plain
    // single-statement insert is already atomic at the D1 level.
    await db.insert(numberingSeries).values(insertValues);
  }

  const inserted = await db
    .select()
    .from(numberingSeries)
    .where(eq(numberingSeries.id, id))
    .get();

  if (!inserted) {
    throw new Error("NOT_FOUND: Inserted series disappeared");
  }

  return {
    id: inserted.id,
    module: inserted.module,
    doctype: inserted.doctype,
    seriesName: inserted.seriesName,
    prefix: inserted.prefix,
    currentValue: inserted.currentValue,
    companyId: inserted.companyId,
    resetEvery: inserted.resetEvery as "never" | "yearly" | "monthly",
    autoGenerate: inserted.autoGenerate,
    isActive: inserted.isActive,
    isDefault: inserted.isDefault,
    updatedAt: inserted.updatedAt,
  };
}

/**
 * Partial-update a series by its primary key id. Company-scoped: if the row
 * doesn't belong to the claimed company, returns null (defence-in-depth
 * beyond the ordinary authz layer in the route handler).
 */
export async function updateSeriesById(
  db: DrizzleD1Database,
  params: { id: string; companyId: string } & UpdateDoctypeSeriesParams,
): Promise<DoctypeSeriesRow | null> {
  const existing = await db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.id, params.id),
        eq(numberingSeries.companyId, params.companyId),
      ),
    )
    .get();

  if (!existing) return null;

  const patch: Record<string, unknown> = { ...updateTimestamps() };
  if (params.prefix !== undefined) patch.prefix = params.prefix;
  if (params.seriesName !== undefined) patch.seriesName = params.seriesName;
  if (params.resetEvery !== undefined) patch.resetEvery = params.resetEvery;
  if (params.autoGenerate !== undefined)
    patch.autoGenerate = params.autoGenerate ? 1 : 0;
  if (params.isActive !== undefined) patch.isActive = params.isActive ? 1 : 0;
  if (params.updatedBy) patch.updatedBy = params.updatedBy;

  await db
    .update(numberingSeries)
    .set(patch)
    .where(eq(numberingSeries.id, params.id));

  const after = await db
    .select()
    .from(numberingSeries)
    .where(eq(numberingSeries.id, params.id))
    .get();
  if (!after) return null;

  return {
    id: after.id,
    module: after.module,
    doctype: after.doctype,
    seriesName: after.seriesName,
    prefix: after.prefix,
    currentValue: after.currentValue,
    companyId: after.companyId,
    resetEvery: after.resetEvery as "never" | "yearly" | "monthly",
    autoGenerate: after.autoGenerate,
    isActive: after.isActive,
    isDefault: after.isDefault,
    updatedAt: after.updatedAt,
  };
}

/**
 * Make `params.id` the default series for its (module, doctype, company)
 * triple, flipping any other default row to 0 in the same logical step.
 *
 * Strict ordering:
 *   1. Look up the target row (must exist + belong to company).
 *   2. UPDATE others SET is_default = 0 WHERE triple matches AND id != target.
 *   3. UPDATE target SET is_default = 1.
 *
 * The unset-first / set-second order matters — flipping the target on FIRST
 * would violate the partial UNIQUE for a single statement in isolation. D1
 * does not expose multi-statement transactions from the request path, so
 * this is the best we can do; concurrent callers will serialise on the
 * unique-index check and at most one wins.
 *
 * Returns the updated target row, or null if no row matched.
 */
export async function setDefaultSeriesById(
  db: DrizzleD1Database,
  params: { id: string; companyId: string; updatedBy?: string },
): Promise<DoctypeSeriesRow | null> {
  const target = await db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.id, params.id),
        eq(numberingSeries.companyId, params.companyId),
      ),
    )
    .get();

  if (!target) return null;

  // If target is already default, no-op — but still return the row so the
  // caller can safely treat this as idempotent.
  if (target.isDefault === 1) {
    return {
      id: target.id,
      module: target.module,
      doctype: target.doctype,
      seriesName: target.seriesName,
      prefix: target.prefix,
      currentValue: target.currentValue,
      companyId: target.companyId,
      resetEvery: target.resetEvery as "never" | "yearly" | "monthly",
      autoGenerate: target.autoGenerate,
      isActive: target.isActive,
      isDefault: target.isDefault,
      updatedAt: target.updatedAt,
    };
  }

  const updatedBy = params.updatedBy ?? null;
  const auditPatch = {
    ...updateTimestamps(),
    ...(updatedBy ? { updatedBy } : {}),
  };

  // Unset-then-set in a SINGLE D1 request via db.batch. Without this, if
  // step 1 succeeded and step 2 failed (worker eviction, binding blip,
  // constraint glitch), the triple would end up with ZERO defaults —
  // generateNextNumber would then silently fall back or fail. Drizzle's
  // batch on D1 is transactional: all or nothing.
  await runAtomicBatch(db, [
    // Step 1: Unset existing defaults in same triple.
    db
      .update(numberingSeries)
      .set({ isDefault: 0, ...auditPatch })
      .where(
        and(
          eq(numberingSeries.module, target.module),
          eq(numberingSeries.doctype, target.doctype),
          eq(numberingSeries.companyId, params.companyId),
          eq(numberingSeries.isDefault, 1),
        ),
      ),
    // Step 2: Set target. The order matters — target is "flipping ON",
    // and D1 applies batched statements sequentially in order, so the
    // partial UNIQUE check on the second UPDATE sees the triple with
    // every other row already at is_default=0.
    db
      .update(numberingSeries)
      .set({ isDefault: 1, ...auditPatch })
      .where(eq(numberingSeries.id, params.id)),
  ]);

  const after = await db
    .select()
    .from(numberingSeries)
    .where(eq(numberingSeries.id, params.id))
    .get();
  if (!after) return null;

  return {
    id: after.id,
    module: after.module,
    doctype: after.doctype,
    seriesName: after.seriesName,
    prefix: after.prefix,
    currentValue: after.currentValue,
    companyId: after.companyId,
    resetEvery: after.resetEvery as "never" | "yearly" | "monthly",
    autoGenerate: after.autoGenerate,
    isActive: after.isActive,
    isDefault: after.isDefault,
    updatedAt: after.updatedAt,
  };
}

/**
 * Delete a series by id (company-scoped). If the deleted row was the default
 * and other series remain in the same triple, the OLDEST remaining row is
 * promoted to default so the doctype is never left without one (which would
 * break generateNextNumber).
 */
export async function deleteSeriesById(
  db: DrizzleD1Database,
  params: { id: string; companyId: string },
): Promise<boolean> {
  const target = await db
    .select()
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.id, params.id),
        eq(numberingSeries.companyId, params.companyId),
      ),
    )
    .get();

  if (!target) return false;

  // When the target is the default row, promote the next-oldest sibling
  // ATOMICALLY with the delete so the triple is never left defaultless.
  // Look up the sibling BEFORE the batch so we know whether to include a
  // promote statement. Note: the sibling must NOT be the target itself
  // (findFirst ordered-by-createdAt would otherwise return the doomed row).
  if (target.isDefault === 1) {
    const sibling = await db
      .select({ id: numberingSeries.id })
      .from(numberingSeries)
      .where(
        and(
          eq(numberingSeries.module, target.module),
          eq(numberingSeries.doctype, target.doctype),
          eq(numberingSeries.companyId, params.companyId),
          // Exclude the row being deleted — it may still be visible to
          // this SELECT until the DELETE statement runs inside the batch.
          not(eq(numberingSeries.id, params.id)),
        ),
      )
      .orderBy(numberingSeries.createdAt)
      .limit(1);

    if (sibling.length > 0) {
      // Delete-then-promote in one D1 transaction. Order matters because
      // of the partial UNIQUE (module, doctype, company_id) WHERE
      // is_default = 1 — promoting the sibling while the target still
      // holds is_default = 1 would violate the constraint. So:
      //   1. DELETE target (frees the UNIQUE slot)
      //   2. UPDATE sibling SET is_default = 1 (safely claims it)
      // In D1 this runs as one atomic transaction — if step 2 fails for
      // any reason, step 1 is rolled back, preserving the old default.
      await runAtomicBatch(db, [
        db.delete(numberingSeries).where(eq(numberingSeries.id, params.id)),
        db
          .update(numberingSeries)
          .set({ isDefault: 1, ...updateTimestamps() })
          .where(eq(numberingSeries.id, sibling[0]!.id)),
      ]);
      return true;
    }
    // No sibling — the delete IS the final state. A future insert for
    // this triple will adopt is_default=1 (see createDoctypeSeries).
  }

  await db.delete(numberingSeries).where(eq(numberingSeries.id, params.id));
  return true;
}
